<template>
  <NuxtLink
    :to="localizedHref"
    class="font-mplus block w-full max-w-sm text-inherit no-underline hover:no-underline focus:no-underline"
    @click="onOpen"
  >
    <article ref="cardRef" class="cursor-pointer">
      <div class="relative overflow-hidden rounded-3xl bg-gray-200">
        <div class="aspect-square">
          <div class="relative h-full w-full bg-gray-200">
            <div
              v-if="!imageLoaded"
              class="absolute inset-0 animate-pulse bg-gray-200"
            ></div>
            <img
              ref="imageRef"
              :src="currentImageUrl"
              :alt="establishmentName"
              class="h-full w-full object-cover transition-opacity duration-300"
              :class="imageLoaded ? 'opacity-100' : 'opacity-0'"
              loading="lazy"
              decoding="async"
              fetchpriority="low"
              width="600"
              height="600"
              @load="onImageLoad"
              @error="onImageError"
            />
          </div>
        </div>

        <div
          v-if="upcomingEvents && upcomingEvents > 0"
          class="absolute bottom-4 left-4 rounded-lg bg-pink-500 px-4 py-2 text-sm font-bold tracking-wide text-white shadow"
        >
          {{ upcomingEvents + " " + $t("establishmentCard.events") }}
        </div>
      </div>

      <!-- ✅ Text content: HARD reset + controlled spacing -->
      <div class="px-1 pt-4">
        <div class="event-stack">
          <h3 class="event-title truncate font-rounded font-bold uppercase">
            {{ establishmentName }}
          </h3>
          <h3 class="text-xs font-medium">
            {{ city?.cityName }}, {{ country?.countryName }}
          </h3>
          <div
            class="mt-4 flex w-full flex-col items-start gap-2 overflow-x-hidden lg:flex-row lg:items-center"
          >
            <div
              v-for="(cat, i) in category ?? []"
              :key="i"
              class="inline-flex flex-shrink-0 items-center gap-1"
            >
              <Icon class="h-3 w-3 text-purple-950" :name="cat" />
              <span
                class="whitespace-nowrap text-xs font-medium text-purple-950"
                :title="cat"
                >{{ cat }}</span
              >
            </div>
          </div>
        </div>
      </div>
    </article>
  </NuxtLink>
</template>

<script setup lang="ts">
import {
  computed,
  ref,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
} from "vue";
import { useAnalytics } from "~/composables/useAnalytics";
import Icon from "~/components/Icon.vue";
import type {
  CreateCityResponse,
  CreateCountryResponse,
} from "~~/shared/models";

type Props = {
  id?: string | number;
  slug?: string;
  href?: string;
  imageUrl?: string;
  establishmentName?: string;
  upcomingEvents?: number;
  overallEvents?: number;
  followers?: number;
  category?: string[];
  city?: CreateCityResponse;
  country?: CreateCountryResponse;
};

const props = defineProps<Props>();
const emit = defineEmits<{ (e: "open", payload: Props): void }>();
const localePath = useLocalePath();
const { trackViewed, trackClicked } = useAnalytics();
const imageRef = ref<HTMLImageElement | null>(null);
const imageLoaded = ref(false);
const fallbackImageUrl = "/images/placeholder-soft-neutral-venue.svg";
const currentImageUrl = ref(fallbackImageUrl);

// IntersectionObserver for view-impression tracking (fire once per card)
const cardRef = ref<HTMLElement | null>(null);
let viewTracked = false;
let observer: IntersectionObserver | null = null;

onMounted(() => {
  syncImageState();
  const elId = props.id ? String(props.id) : null;
  if (!cardRef.value || !elId) return;
  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && !viewTracked && elId) {
          viewTracked = true;
          trackViewed("establishment", elId);
          observer?.disconnect();
        }
      }
    },
    { threshold: 0.5 },
  );
  observer.observe(cardRef.value);
});

onBeforeUnmount(() => {
  observer?.disconnect();
  observer = null;
});

watch(
  () => props.imageUrl,
  async (nextUrl) => {
    imageLoaded.value = false;
    currentImageUrl.value = nextUrl?.trim() || fallbackImageUrl;
    await nextTick();
    syncImageState();
  },
  { immediate: true },
);

function onImageLoad() {
  imageLoaded.value = true;
}

function syncImageState() {
  if (!imageRef.value?.complete) return;
  if (imageRef.value.naturalWidth > 0) imageLoaded.value = true;
  else onImageError();
}

function onImageError() {
  if (currentImageUrl.value !== fallbackImageUrl) {
    imageLoaded.value = false;
    currentImageUrl.value = fallbackImageUrl;
    return;
  }

  imageLoaded.value = true;
}

function buildHref() {
  if (props.href) return props.href;
  const slug = props.slug ?? (props.id ? String(props.id) : null);
  if (slug) return `/establishment/${slug}`;
  return null;
}

const rawHref = computed(() => buildHref());
const localizedHref = computed(() => localePath(rawHref.value ?? "/"));

function onOpen(event?: Event) {
  emit("open", props);
  const elId = props.id ? String(props.id) : null;
  if (elId) trackClicked("establishment", elId);

  if (!rawHref.value) {
    event?.preventDefault();
  }
}
</script>
